import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Instagram, Menu, X, Info, Building2, Wrench, Home as HomeIcon } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  K. Elman Construction — concept site. Dumaguete/Valencia builder covering
  residential construction, multi-door apartments and renovation/interior fit-out.
  Moody dark photographic identity built around real jobsite photography.
*/

const RED = "#B3242A";
const BLACK = "#111111";
const CREAM = "#F5F3EF";
const INK = "#1A1A1A";

const FB = "https://www.facebook.com/K.ElmanConstruction/";
const IG = "https://www.instagram.com/reel/DSUTY7sEUFL/";
const NAME = "K. Elman Construction";
const SLUG = "k-elman-construction-concept";
const P = "/construction/kelman";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function KElmanConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["What We Build", "#build"], ["Our Work", "#work"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Archivo', sans-serif" }} data-testid="kelman-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700;800&display=swap');
        #top h1, #top h2, #top h3 { font-family: 'Archivo', sans-serif; font-stretch: condensed; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 bg-black transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.3)]" : ""}`}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="ke-logo" className="text-white font-extrabold text-lg tracking-tight">
            K.<span style={{ color: RED }}>ELMAN</span> <span className="text-white/60 font-medium text-sm">CONSTRUCTION</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-semibold text-white/75 hover:text-white">{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="ke-header-cta" className="hidden sm:inline-flex px-5 py-2.5 text-sm font-bold text-white" style={{ background: RED }}>
            Get a Quote
          </a>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="ke-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1 bg-black">
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 text-white font-semibold">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="ke-mobile-cta" className="block mt-2 text-center px-5 py-3 text-sm font-bold text-white" style={{ background: RED }}>Get a Quote</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end" data-testid="ke-hero">
        <img src={`${P}/hero-banner.jpg`} alt="K. Elman Construction jobsite" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "72% center" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,17,17,0.2) 0%, rgba(17,17,17,0.7) 65%, rgba(17,17,17,0.95) 100%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-40 w-full">
          <motion.div {...fadeUp} className="max-w-2xl bg-black/45 border-l-4 p-6 sm:p-8" style={{ borderColor: RED }}>
            <span className="text-xs font-bold uppercase tracking-[0.25em] drop-shadow" style={{ color: "#FF9E96" }}>Valencia &amp; Dumaguete City, Negros Oriental</span>
            <h1 className="mt-4 text-5xl sm:text-6xl leading-tight text-white font-extrabold">Built Right, On Your Lot.</h1>
            <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
              Residential construction, multi-door apartment builds, renovation and interior fit-out — led by Engineer Kerwin Elman across Valencia, Dumaguete and Tanjay.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="ke-hero-cta" className="px-7 py-3.5 text-sm font-bold text-white" style={{ background: RED }}>Get a Quote</a>
              <a href="#work" data-testid="ke-hero-work" className="border border-white/40 px-7 py-3.5 text-sm font-bold text-white hover:bg-white/10 transition-colors">See Our Work</a>
            </div>
            <p className="mt-7 text-sm text-white/70" data-testid="ke-service-area">Serving Valencia, Dumaguete and Tanjay</p>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE BUILD */}
      <section id="build" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>What We Build</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold leading-snug">Homes, Apartments &amp; Renovations</h2>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-10">
          {[
            [HomeIcon, "Residential Construction", "New home builds across Valencia, Dumaguete and Tanjay, sized to your lot and budget."],
            [Building2, "Multi-Door Apartments", "Multi-unit apartment builds for owners looking to build rental income into their property."],
            [Wrench, "Renovation & Interior Fit-Out", "Renovation and interior fit-out work for homes and commercial spaces that need a refresh."],
          ].map(([Icon, t, b]) => (
            <motion.div key={t} {...fadeUp}>
              <Icon size={26} style={{ color: RED }} />
              <h3 className="mt-4 text-lg font-bold">{t}</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: `${INK}90` }}>{b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WORK */}
      <section id="work" className="py-20 md:py-28 bg-black">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>On Site</span>
            <h2 className="mt-3 text-4xl sm:text-5xl text-white font-extrabold leading-snug">Our Work</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { img: `${P}/multistorey-project.jpg`, label: "Multi-storey structural pour in progress" },
              { img: `${P}/pour-project.jpg`, label: "Concrete pour, residential build" },
              { img: `${P}/foundation-project.jpg`, label: "Foundation and wall work underway" },
            ].map((it) => (
              <motion.div key={it.img} {...fadeUp} data-testid="ke-work-item" className="overflow-hidden">
                <img src={it.img} alt={it.label} className="w-full h-64 object-cover" loading="lazy" />
                <p className="mt-3 text-sm text-white/70">{it.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>Get In Touch</span>
            <h2 className="mt-3 text-4xl font-extrabold leading-snug">Request a Quote</h2>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Address</div><div>Dumaguete–Palinpinon Road, Valencia, Negros Oriental</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Phone</div><a href="tel:+639177219061" className="hover:underline">0917 721 9061</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Mail size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Email</div><a href="mailto:k.elmanconstruction@gmail.com" className="hover:underline">k.elmanconstruction@gmail.com</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: RED }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="ke-facebook-link" className="hover:underline">facebook.com/K.ElmanConstruction</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="ke"
              accent={RED}
              accentText="#FFFFFF"
              heading="Request a Quote"
              subheading="New build, apartment or renovation — tell us about the project and we'll follow up."
              projectTypes={["New House Build", "Multi-Door Apartment", "Renovation / Interior Fit-Out", "Other"]}
              phone="+639177219061"
              phoneDisplay="0917 721 9061"
              messengerUrl={FB}
              email="k.elmanconstruction@gmail.com"
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: BLACK, color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-10">
          <div>
            <p className="text-white text-lg font-extrabold">K.<span style={{ color: RED }}>ELMAN</span> CONSTRUCTION</p>
            <p className="mt-4 text-sm leading-relaxed">Residential construction, apartments and renovation across Valencia, Dumaguete and Tanjay.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">Dumaguete–Palinpinon Road,<br />Valencia, Negros Oriental</p>
            <p className="text-sm mt-2">0917 721 9061</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
              <a href={IG} target="_blank" rel="noopener noreferrer" aria-label="Instagram"><Instagram size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Project details and availability should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-bold text-white shadow-xl" style={{ background: RED }}>
        Message {NAME}
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
